// Device detection and image processing utilities

export function detectDeviceType(width, height) {
  const aspectRatio = width / height;
  const area = width * height;
  
  console.log(`Detecting device type: ${width}x${height}, aspect ratio: ${aspectRatio.toFixed(2)}, area: ${area}`);
  
  // PRIORITY 0: Check for extreme portrait ratios FIRST (mobile phones are tall)
  // Modern phones can have heights > 800px but are still mobile due to extreme portrait ratio
  // If aspect ratio is very portrait (< 0.55) and width is still mobile-sized (< 1300px), it's mobile
  if (aspectRatio < 0.55 && width < 1300) {
    console.log(`✅ Detected as MOBILE (extreme portrait ratio): aspect=${aspectRatio.toFixed(2)} < 0.55 AND width=${width} < 1300`);
    return 'mobile';
  }
  
  // PRIORITY 1: Check absolute dimensions first (more reliable than aspect ratio)
  // Desktop detection - large dimensions take precedence over aspect ratio
  // A screenshot is desktop if ANY of these are true:
  // - Width > 1025px (wide desktop screens) AND not extreme portrait
  // - Height > 801px (tall desktop screens) AND not extreme portrait
  // - Area > 2,000,000 pixels (3MP+ images are almost always desktop)
  if (width > 1025 || height > 801 || area > 2000000) {
    console.log(`✅ Detected as DESKTOP (large dimensions): width=${width} > 1025 OR height=${height} > 801 OR area=${area} > 2000000`);
    return 'desktop';
  }
  
  // Ultra-wide screen detection (21:9, 32:9, etc.)
  // Ultra-wide screens have very high aspect ratios (2.0+) and large dimensions
  if (aspectRatio >= 2.0 && (width > 2000 || height > 1000)) {
    console.log('Detected as ultra-wide desktop (very high aspect ratio with large dimensions)');
    return 'ultrawide';
  }
  
  // PRIORITY 2: Mobile detection with extreme aspect ratios AND small dimensions
  // Only classify as mobile if BOTH conditions are met:
  // 1. Extreme aspect ratio (very portrait or very landscape)
  // 2. Small dimensions (typical mobile screen size)
  if ((aspectRatio >= 2.0 || aspectRatio <= 0.5) && width <= 768 && height <= 900) {
    console.log('Detected as mobile (extreme aspect ratio + small dimensions)');
    return 'mobile';
  }
  
  // Small screens are definitely mobile
  if (width <= 768 || height <= 600) {
    console.log('Detected as mobile (small dimensions)');
    return 'mobile';
  }
  
  // Medium screens with moderate aspect ratios are likely tablets
  if (width > 800 && width <= 1400 && height > 600 && height <= 1000) {
    // Check if it's more tablet-like (4:3 or 3:4 aspect ratio)
    if (aspectRatio >= 0.7 && aspectRatio <= 1.4) {
      console.log('Detected as tablet (medium dimensions, moderate aspect ratio)');
      return 'tablet';
    }
  }
  
  // PRIORITY 3: Medium-large screens (likely desktop) - width OR height over threshold
  if (width > 1200 || height > 1200) {
    console.log('Detected as desktop (medium-large dimensions)');
    return 'desktop';
  }
  
  // Default fallback based on width (most reliable single metric)
  if (width > 1024) {
    console.log('Defaulting to desktop (large width)');
    return 'desktop';
  } else if (width > 768) {
    console.log('Defaulting to tablet (medium width)');
    return 'tablet';
  } else {
    console.log('Defaulting to mobile (small width)');
    return 'mobile';
  }
}

export async function readImageBitmap(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      // Clean up the object URL to prevent memory leaks
      URL.revokeObjectURL(img.src);
    };
    img.onerror = (error) => {
      console.error('Error loading image in Safari:', error);
      reject(error);
    };
    
    // Safari-specific: Add crossOrigin attribute for better compatibility
    img.crossOrigin = 'anonymous';
    img.src = URL.createObjectURL(file);
  });
}

export function getDeviceDimensions(deviceType) {
  const dimensions = {
    mobile: { width: 375, height: 812 },
    tablet: { width: 768, height: 1024 },
    desktop: { width: 1920, height: 1080 },
    ultrawide: { width: 3440, height: 1440 }
  };
  
  return dimensions[deviceType] || dimensions.desktop;
}

export function getDeviceName(deviceType) {
  const names = {
    mobile: 'Mobile',
    tablet: 'Tablet',
    desktop: 'Desktop',
    ultrawide: 'Ultra-wide'
  };
  
  return names[deviceType] || names.desktop;
}

export function getSpecificDeviceName(deviceType) {
  const names = {
    mobile: 'iPhone 17 Pro',
    tablet: 'iPad Pro',
    desktop: 'MacBook Pro',
    ultrawide: 'Apple Pro Display XDR'
  };
  
  return names[deviceType] || names.desktop;
}

// Function to detect device types for multiple files
export async function detectDeviceTypesForFiles(files) {
  const deviceTypes = [];
  const deviceNames = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const { width, height } = await readImageBitmap(files[i]);
      const deviceType = detectDeviceType(width, height);
      const deviceName = getDeviceName(deviceType);
      
      deviceTypes.push(deviceType);
      deviceNames.push(deviceName);
      
      console.log(`File ${i + 1} (${files[i].name}): ${width}x${height} -> ${deviceType} (${deviceName})`);
    } catch (error) {
      console.error(`Error processing file ${i + 1}:`, error);
      // Default to desktop if detection fails
      deviceTypes.push('desktop');
      deviceNames.push('MacBook Pro');
    }
  }
  
  return { deviceTypes, deviceNames };
}